// apps/periverse/src/routes/_auth/find-asset.tsx
import { loadRemote } from '@module-federation/runtime';
import { Box, CircularProgress } from '@mui/material';
import { createFileRoute, redirect, useSearch } from '@tanstack/react-router';
import React from 'react';
import { AssetType } from '@peritasai/types';
import { Permission } from '../../features/auth';
import { env } from '../../shared/utils/env';

type RemoteComponentModule = {
  default: React.ComponentType<any>; // Use 'any' for props if they aren't defined elsewhere
};

const loadTrackAssetModule = () => loadRemote('peri_track/TrackAssetModule');

// 2. Wrap the loader function with React.lazy and apply the assertion
const TrackAssetModule = React.lazy(async () => {
  // Assert the type of the resolved module here:
  const remoteModule = (await loadTrackAssetModule()) as RemoteComponentModule;

  // Now TypeScript knows remoteModule has a 'default' property.
  return { default: remoteModule.default };
});
export const Route = createFileRoute('/_auth/find-asset')({
  staticData: {
    title: 'pages.trackAsset.title',
    description: 'pages.trackAsset.description',
  },
  validateSearch: (search: Record<string, unknown>) => ({
    search: search.search as string | undefined,
    type: search.type as AssetType | undefined,
  }),
  beforeLoad: async ({ context }) => {
    const { isAuthenticated, hasPermission, isAdmin } = context.auth;

    if (!isAuthenticated) throw redirect({ to: '/' });
    if (!hasPermission(Permission.ASSET_FINDER) && !isAdmin()) throw redirect({ to: '/403' });
  },
  component: TrackAsset,
});

function TrackAsset() {
  const { search: searchQuery, type } = useSearch({
    from: '/_auth/find-asset',
  });

  return (
    <React.Suspense
      fallback={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
          }}
        >
          <CircularProgress size={40} color="primary" />
        </Box>
      }
    >
      <Box
        sx={{
          pt: { xs: 2, md: '1rem' },
          pb: { xs: 2, md: '1.5rem' },
          pl: { xs: 2, md: '1.5rem', lg: '3rem' },
          pr: { xs: 2, md: '1.5rem', lg: '3rem' },
        }}
      >
        <TrackAssetModule
          searchQuery={searchQuery || undefined}
          initialType={type}
          showDevToggle={env.SHOW_TRACK_ASSET_DEV_TOGGLE}
        />
      </Box>
    </React.Suspense>
  );
}
